import { useId, useMemo } from 'react';
import { useI18n } from '@/shared/i18n/useT';
import { useBookingT } from './i18n';
import { timeZoneOptions, zoneShortLabel } from './datetime';

interface Props {
  value: string;
  visitorZone: string;
  professionalZone: string;
  onChange: (timeZone: string) => void;
}

/**
 * The single most expensive mistake on a booking page is reading a time in the
 * wrong zone, so the zone is a visible control rather than a footnote. It
 * defaults to the visitor's own zone; the professional's is always one option
 * away.
 */
export function TimezoneSelect({
  value,
  visitorZone,
  professionalZone,
  onChange,
}: Props) {
  const t = useBookingT();
  const { formatLocale } = useI18n();
  const id = useId();

  const zones = useMemo(
    () => timeZoneOptions(visitorZone, professionalZone),
    [professionalZone, visitorZone],
  );

  const suffix = (zone: string): string => {
    if (zone === visitorZone) return ` — ${t('publicBooking.timezone.yours')}`;
    if (zone === professionalZone)
      return ` — ${t('publicBooking.timezone.professional')}`;
    return '';
  };

  return (
    <div className="flex flex-col gap-1">
      <label htmlFor={id} className="text-xs font-medium text-muted">
        {t('publicBooking.timezone.label')}
      </label>
      <select
        id={id}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className="nt-control nt-control-select w-full text-sm"
      >
        {zones.map((zone) => (
          <option key={zone} value={zone}>
            {`${zone.replaceAll('_', ' ')} (${zoneShortLabel(zone, formatLocale)})${suffix(zone)}`}
          </option>
        ))}
      </select>
    </div>
  );
}
